import { Box, Button, FormControl, FormLabel, InputLabel, MenuItem, Paper, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material"
import React from "react"
import { useTranslation } from "react-i18next"
import { HeaderBox, HeaderSeparateTypography, HeaderTypography } from "./QuotationDetailList.styles"
import { formatPrice } from "~/src/utils/formatPrice"
import { TableLabelText } from "~/src/components/molecules"
import DownloadIcon from '@mui/icons-material/Download'
import { convertQuotationStatusText } from "~/src/utils/convert"

interface IQuotationDetailProps {
  data?: IQuotationDetailItem[]
  loadingPorts: ILoadingPort[]
  loadingPortId: number
  changeLoadingId: (loadingPortId: Number) => void
}

interface IQuotationDetailItem {
  id: number
  refNo: string
  displayName: string
  category: string
  withCatalyst?: string
  price: number
  isPurchased: boolean
  isSold: boolean
  loadingPorts: [{
    id: number
    transportationFee: number
  }],
}

interface ILoadingPort {
  id: number
  name: string
}

export const QuotationDetailList: React.FC<IQuotationDetailProps> = ({
  data,
  loadingPorts,
  loadingPortId,
  changeLoadingId
}): JSX.Element => {
  const { t } = useTranslation()

  const handlerChangeLoadingPortId = (event: React.ChangeEvent<HTMLInputElement>) => {
    changeLoadingId(event.target.value)
  }

  return (
    <Paper>
      <Stack direction={'row'} p={2} spacing={2}>
        <FormControl>
          <InputLabel sx={{ fontSize: '14px', background: 'white', px: '5px' }}>{t('quotation_detail.loading_port')}</InputLabel>
          <Select
            value={loadingPortId}
            size={'small'}
            renderValue={(value) => 
              loadingPorts.find((loadingPort) => loadingPort.id === Number(value))?.name
            }
            onChange={handlerChangeLoadingPortId}
            sx={{
              minWidth: '220px',
              fontSize: '16px',
              lineHeight: '24px',
              fontStyle: 'normal',
              background: 'white',
              height: '40px',
            }}
          >
            {loadingPorts.map((loadingPort) => (
              <MenuItem key={loadingPort.id} value={loadingPort.id}>
                {loadingPort.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={
            <DownloadIcon />
          }
          sx={{display: 'none'}}
        >
          <Typography>
            {t('quotation_detail.excel_export')}
          </Typography>
        </Button>
      </Stack>
      <TableContainer style={{ maxHeight: 600 }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell style={{ background: '#FFFFFF' }}>
                  <HeaderBox>
                    <HeaderTypography>
                      {t('quotation_detail.ref_no')}  
                    </HeaderTypography>
                    <HeaderSeparateTypography />
                  </HeaderBox>
              </TableCell>
              <TableCell style={{ background: '#FFFFFF' }}>
                  <HeaderBox>
                    <HeaderTypography>
                      {t('quotation_detail.product_name')}  
                    </HeaderTypography>
                    <HeaderSeparateTypography />
                  </HeaderBox>
              </TableCell>
              <TableCell style={{ background: '#FFFFFF' }}>
                  <HeaderBox>
                    <HeaderTypography>
                      {t('quotation_detail.category_name')}  
                    </HeaderTypography>
                    <HeaderSeparateTypography />
                  </HeaderBox>
              </TableCell>
              <TableCell style={{ background: '#FFFFFF' }}>
                  <HeaderBox>
                    <HeaderTypography>
                      {t('quotation_detail.option')}  
                    </HeaderTypography>
                    <HeaderSeparateTypography />
                  </HeaderBox>
              </TableCell>
              <TableCell style={{ background: '#FFFFFF' }}>
                  <HeaderBox>
                    <HeaderTypography>
                      {t('quotation_detail.price')}  
                    </HeaderTypography>
                    <HeaderSeparateTypography />
                  </HeaderBox>
              </TableCell>
              <TableCell style={{ background: '#FFFFFF' }}>
                  <HeaderBox>
                    <HeaderTypography>
                      {t('quotation_detail.status')}  
                    </HeaderTypography>
                    <HeaderSeparateTypography />
                  </HeaderBox>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map((item, index) => (
              <TableRow key={index}>
                <TableCell>
                  <TableLabelText>
                    {item.refNo}
                  </TableLabelText>
                </TableCell>
                <TableCell>
                  <TableLabelText>
                    {item.displayName}
                  </TableLabelText>
                </TableCell>
                <TableCell>
                  <TableLabelText>
                    {item.category}
                  </TableLabelText>
                </TableCell>
                <TableCell>
                  <TableLabelText>
                    {item.withCatalyst === null ? '' : (item.withCatalyst ? 'With Catalyst' : 'Without Catalyst')}
                  </TableLabelText>
                </TableCell>
                <TableCell>
                  <TableLabelText>
                    {formatPrice('STOCK', item.price + item.loadingPorts.find(p => p.id === loadingPortId)?.transportationFee)}
                  </TableLabelText>
                </TableCell>
                <TableCell>
                  <TableLabelText>
                    {convertQuotationStatusText(
                      item.loadingPorts.some(loadingPort => loadingPort.id === loadingPortId),
                      item.isPurchased,
                      item.isSold
                    )}
                  </TableLabelText>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}